import { Box, CircularProgress, Backdrop, Typography, LinearProgress, Skeleton, Card, CardContent, Grid } from "@mui/material";

export const Loader = ({ 
  size = "100%", 
  variant = "simple", // "simple", "backdrop", "top", or "skeleton"
  open = true,
  message = "",
  description = "",
  backdropProps = {},
  containerProps = {},
  showLinearProgress = false,
  skeletonCount = 3, // Number of skeleton cards to show
  skeletonVariant = "card" // "card", "list", "table", or "sessionCard"
}) => {
  // Simple loader (existing functionality)
  if (variant === "simple") {
    return (
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          top: "50%",
          width: size,
          height: size,
          transform: "translate(-50%, -50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
        {...containerProps}
      >
        {!showLinearProgress && <CircularProgress />}
        {showLinearProgress && (
          <Box sx={{ width: "80%", mt: 2 }}>
            <LinearProgress 
              variant="indeterminate" 
              sx={{ height: 6, borderRadius: 3 }} 
            />
          </Box>
        )}
      </Box>
    );
  }

  // Backdrop loader (new functionality)
  if (variant === "backdrop") {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1,
          backgroundColor: showLinearProgress 
            ? "rgba(255, 255, 255, 0.9)" 
            : "rgba(0, 0, 0, 0.5)",
          backdropFilter: showLinearProgress ? "blur(4px)" : "none",
        }}
        {...backdropProps}
      >
        {!showLinearProgress && <CircularProgress size={60} />}
        {showLinearProgress && (
          <Box sx={{ width: "80%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <LinearProgress 
              variant="indeterminate" 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "primary.main"
                }
              }} 

            />
            {message && !showLinearProgress &&(
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  color: "text.primary",
                  fontWeight: 500,
                }}
              >
                {message}
              </Typography>
            )}
            {description && !showLinearProgress && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "text.secondary",
                  textAlign: "center",
                  maxWidth: 300,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        )}
        {message && !showLinearProgress && (
          <Typography
            variant="h6"
            sx={{
              mt: 2,
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            {message}
          </Typography>
        )}
        {description && !showLinearProgress && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "text.secondary",
              textAlign: "center",
              maxWidth: 300,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    );
  }

  // Top loader (linear progress at top of page)
  if (variant === "top" && open) {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          width: '100%',
          backgroundColor: '#fefefe8a',
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
        {...containerProps}
      >
        <LinearProgress 
          variant="indeterminate" 
          sx={{ 
            "& .MuiLinearProgress-bar": {
              backgroundColor: "primary.main"
            }
          }} 
        />
      </Box>
    );
  }

  // Skeleton loader for cards, lists, and tables
  if (variant === "skeleton") {
    if (skeletonVariant === "card") {
      return (
        <Box sx={{ p: 2 }} {...containerProps}>
          <Grid container spacing={2}>
            {Array.from({ length: skeletonCount }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="80%" height={20} />
                      </Box>
                      <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="70%" height={24} />
                      </Grid>
                      <Grid item xs={6}>
                        <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="70%" height={24} />
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="text" width="30%" height={16} sx={{ mb: 1 }} />
                      <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    if (skeletonVariant === "sessionCard") {
      return (
        <Box sx={{ p: 2 }} {...containerProps}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Card sx={{ 
                border: '1px solid #e0e0e0',
                position: 'relative',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <CardContent>
                  {/* Header Section - Session ID, EVSE ID, Country/Party/Location, Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      {/* Session ID */}
                      <Skeleton 
                        variant="text" 
                        width="50%" 
                        height={32} 
                        sx={{ mb: 1, fontWeight: 'bold' }} 
                      />
                      {/* EVSE ID */}
                      <Skeleton 
                        variant="text" 
                        width="70%" 
                        height={20} 
                        sx={{ mb: 1 }} 
                      />
                      {/* Country, Party, Location */}
                      <Skeleton 
                        variant="text" 
                        width="80%" 
                        height={20} 
                      />
                    </Box>
                    
                    {/* Status Chip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Skeleton 
                        variant="rectangular" 
                        width={80} 
                        height={32} 
                        sx={{ borderRadius: 16 }} 
                      />
                      {/* Active Session Indicator */}
                      <Skeleton 
                        variant="circular" 
                        width={20} 
                        height={20} 
                      />
                    </Box>
                  </Box>

                  {/* Content Grid - Start Time, End Time, Energy, Duration */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      {/* Start Time */}
                      <Box sx={{ mb: 1 }}>
                        <Skeleton variant="text" width="30%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="80%" height={24} />
                      </Box>
                      
                      {/* End Time (for completed sessions) */}
                      <Box sx={{ mb: 1 }}>
                        <Skeleton variant="text" width="25%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="80%" height={24} />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {/* Energy Consumed */}
                      <Box sx={{ mb: 1 }}>
                        <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="60%" height={24} sx={{ fontWeight: 'bold' }} />
                      </Box>
                      
                      {/* Duration (for completed sessions) */}
                      <Box sx={{ mb: 1 }}>
                        <Skeleton variant="text" width="25%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="50%" height={24} />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Total Cost Section */}
                  <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Skeleton variant="text" width="25%" height={16} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="40%" height={32} sx={{ fontWeight: 'bold' }} />
                  </Box>

                  {/* CDR Status Section */}
                  <Box sx={{ mt: 2 }}>
                    <Skeleton variant="text" width="20%" height={16} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 12 }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      );
    }

    if (skeletonVariant === "list") {
      return (
        <Box sx={{ p: 2 }} {...containerProps}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
                <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
              </Box>
              <Skeleton variant="text" width="100%" height={16} />
            </Box>
          ))}
        </Box>
      );
    }

    if (skeletonVariant === "table") {
      return (
        <Box sx={{ p: 2 }} {...containerProps}>
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
          </Box>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Skeleton variant="text" width="20%" height={24} sx={{ mr: 2 }} />
                <Skeleton variant="text" width="25%" height={24} sx={{ mr: 2 }} />
                <Skeleton variant="text" width="20%" height={24} sx={{ mr: 2 }} />
                <Skeleton variant="text" width="15%" height={24} sx={{ mr: 2 }} />
                <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    if (skeletonVariant === "kpi") {
      return (
        <Box sx={{ p: 2 }} {...containerProps}>
          <Grid container spacing={2}>
            {Array.from({ length: skeletonCount }).map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="90%" height={32} />
                      </Box>
                      <Skeleton variant="circular" width={40} height={40} />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Skeleton variant="circular" width={16} height={16} sx={{ mr: 1 }} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }
  }

  // Default fallback
  return null;
};
